import { useEffect, useMemo, useState } from 'react';

/**
 * Reveal an answer string word-by-word so a one-shot demo answer reads as if it
 * streamed. Word- (not char-) granularity means ReactMarkdown never re-parses a
 * broken token mid-reveal. Pace is ~24ms/word, capped so the whole reveal never
 * exceeds ~1.2s regardless of length.
 *
 * When `enabled` is false (reduced motion, or a restored/older turn) it returns
 * the full text with `done: true` immediately — no animation.
 */
const PER_WORD_MS = 24;
const MAX_TOTAL_MS = 1200;

export function useTypewriter(text: string, enabled: boolean): { shown: string; done: boolean } {
  // Split into words that keep their trailing whitespace so a prefix join is
  // byte-exact against the original (no lost spaces/newlines).
  const words = useMemo(() => text.match(/\S+\s*/g) ?? [], [text]);
  const total = words.length;
  const [count, setCount] = useState<number>(enabled ? 0 : total);

  useEffect(() => {
    if (!enabled || total === 0) {
      setCount(total);
      return;
    }
    setCount(0);
    const perWord = Math.min(PER_WORD_MS, MAX_TOTAL_MS / total);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setCount(i);
      if (i >= total) clearInterval(id);
    }, perWord);
    return () => clearInterval(id);
  }, [text, enabled, total]);

  const done = count >= total;
  const shown = done ? text : words.slice(0, count).join('');
  return { shown, done };
}
